<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import api from "@/axios";
import { getBaseUrl } from "@/helpers/baseUrl";
import moment from "moment";

const STATUS_LABELS = {
  aberto:          { label: "Aberto",          cls: "bg-danger" },
  em_atendimento:  { label: "Em Atendimento",  cls: "bg-warning" },
  fechado:         { label: "Fechado",          cls: "bg-success" },
  cancelado:       { label: "Cancelado",        cls: "bg-secondary" },
};

export default {
  components: { Layout, PageHeader },

  page: {
    title: "Chamados WhatsApp",
    meta: [{ name: "description", content: "Chamados WhatsApp" }],
  },

  data() {
    return {
      title: "Chamados WhatsApp",
      items: [
        { text: "CRM", href: "/" },
        { text: "Chamados WhatsApp", active: true },
      ],

      chamados: [],
      total: 0,
      page: 1,
      perPage: 25,
      loading: false,

      filtros: {
        di: moment().subtract(30, "days").format("YYYY-MM-DD"),
        df: moment().format("YYYY-MM-DD"),
        phone: "",
        credencial_cnpj: "",
        titulo: "",
        descricao: "",
        status: "",
      },

      showModal: false,
      modalChamado: null,
      novoStatus: "",
      savingStatus: false,
      saveStatusMsg: null,
    };
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    },
    paginaInicio() {
      return this.total === 0 ? 0 : (this.page - 1) * this.perPage + 1;
    },
    paginaFim() {
      return Math.min(this.page * this.perPage, this.total);
    },
    pageNumbers() {
      const total = this.totalPages;
      const cur = this.page;
      const delta = 2;
      const range = [];
      for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {
        range.push(i);
      }
      return range;
    },
  },

  mounted() {
    this.buscarChamados();
  },

  methods: {
    async buscarChamados(resetPage = false) {
      if (resetPage) this.page = 1;
      this.loading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
        };
        if (this.filtros.di)              params.di              = this.filtros.di;
        if (this.filtros.df)              params.df              = this.filtros.df;
        if (this.filtros.phone)           params.phone           = this.filtros.phone;
        if (this.filtros.credencial_cnpj) params.credencial_cnpj = this.filtros.credencial_cnpj;
        if (this.filtros.titulo)          params.titulo          = this.filtros.titulo;
        if (this.filtros.descricao)       params.descricao       = this.filtros.descricao;
        if (this.filtros.status)          params.status          = this.filtros.status;

        const res = await api.get(`${getBaseUrl()}api/wsapi/chamados`, { params });
        this.chamados = res.data.data || [];
        this.total    = res.data.total || 0;
      } catch (e) {
        console.error("Erro ao buscar chamados:", e);
        this.chamados = [];
        this.total    = 0;
      } finally {
        this.loading = false;
      }
    },

    mudarPagina(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.buscarChamados();
    },

    mudarPerPage() {
      this.page = 1;
      this.buscarChamados();
    },

    abrirModal(chamado) {
      this.modalChamado  = chamado;
      this.novoStatus    = chamado.status || "aberto";
      this.saveStatusMsg = null;
      this.showModal     = true;
    },

    fecharModal() {
      this.showModal    = false;
      this.modalChamado = null;
    },

    async salvarStatus() {
      if (!this.modalChamado) return;
      this.savingStatus = true;
      this.saveStatusMsg = null;
      try {
        await api.patch(
          `${getBaseUrl()}api/wsapi/chamados/${this.modalChamado.id}/status`,
          { status: this.novoStatus }
        );
        this.modalChamado.status = this.novoStatus;
        const item = this.chamados.find(c => c.id === this.modalChamado.id);
        if (item) item.status = this.novoStatus;
        this.saveStatusMsg = { type: "success", text: "Status atualizado com sucesso." };
      } catch (e) {
        console.error("Erro ao atualizar status:", e);
        this.saveStatusMsg = { type: "error", text: "Erro ao salvar. Tente novamente." };
      } finally {
        this.savingStatus = false;
      }
    },

    statusBadge(status) {
      return STATUS_LABELS[status] || { label: status, cls: "bg-secondary" };
    },

    formatData(ts) {
      if (!ts) return "—";
      return moment(ts).format("DD/MM/YYYY HH:mm");
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Filtros -->
    <div class="card">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-sm-2">
            <label class="form-label mb-1">Data Inicial</label>
            <input v-model="filtros.di" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-sm-2">
            <label class="form-label mb-1">Data Final</label>
            <input v-model="filtros.df" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-sm-2">
            <label class="form-label mb-1">Telefone</label>
            <input v-model="filtros.phone" type="text" class="form-control form-control-sm" placeholder="55279..." />
          </div>
          <div class="col-sm-2">
            <label class="form-label mb-1">CNPJ Credencial</label>
            <input v-model="filtros.credencial_cnpj" type="text" class="form-control form-control-sm" placeholder="CNPJ" />
          </div>
          <div class="col-sm-2">
            <label class="form-label mb-1">Título</label>
            <input v-model="filtros.titulo" type="text" class="form-control form-control-sm" placeholder="Buscar..." />
          </div>
          <div class="col-sm-2">
            <label class="form-label mb-1">Descrição</label>
            <input v-model="filtros.descricao" type="text" class="form-control form-control-sm" placeholder="Buscar..." />
          </div>
          <div class="col-sm-2">
            <label class="form-label mb-1">Status</label>
            <select v-model="filtros.status" class="form-select form-select-sm">
              <option value="">Todos</option>
              <option value="aberto">Aberto</option>
              <option value="em_atendimento">Em Atendimento</option>
              <option value="fechado">Fechado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div class="col-sm-2 d-flex gap-2">
            <button class="btn btn-primary btn-sm w-100" @click="buscarChamados(true)">
              <i class="bx bx-search me-1"></i>Buscar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid -->
    <div class="card mt-3">
      <div class="card-body">
        <!-- Controles superiores -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center gap-2">
            <label class="mb-0 text-muted small">Registros por página:</label>
            <select v-model="perPage" class="form-select form-select-sm" style="width: auto;" @change="mudarPerPage">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div v-if="!loading" class="text-muted small">
            <span v-if="total > 0">Exibindo {{ paginaInicio }}–{{ paginaFim }} de {{ total }} registros</span>
            <span v-else>Nenhum registro encontrado</span>
          </div>
          <div v-if="loading">
            <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
            <span class="ms-1 text-muted small">Carregando...</span>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-responsive">
          <table class="table table-hover table-sm align-middle">
            <thead class="table-light">
              <tr>
                <th>Título</th>
                <th>Status</th>
                <th>Telefone</th>
                <th>CNPJ Credencial</th>
                <th>Data</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!loading && chamados.length === 0">
                <td colspan="6" class="text-center text-muted py-4">Nenhum chamado encontrado.</td>
              </tr>
              <tr v-for="chamado in chamados" :key="chamado.id">
                <td style="max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  {{ chamado.titulo || "—" }}
                </td>
                <td>
                  <span :class="['badge', statusBadge(chamado.status).cls]">
                    {{ statusBadge(chamado.status).label }}
                  </span>
                </td>
                <td>{{ chamado.contact_phone || "—" }}</td>
                <td>{{ chamado.credencial_cnpj || "—" }}</td>
                <td class="text-nowrap">{{ formatData(chamado["@timestamp"]) }}</td>
                <td class="text-center">
                  <button class="btn btn-outline-secondary btn-sm" @click="abrirModal(chamado)">
                    <i class="bx bx-show"></i> Ver
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: page === 1 }">
              <button class="page-link" @click="mudarPagina(page - 1)">&laquo;</button>
            </li>
            <li v-if="pageNumbers[0] > 1" class="page-item disabled">
              <span class="page-link">…</span>
            </li>
            <li
              v-for="p in pageNumbers"
              :key="p"
              class="page-item"
              :class="{ active: p === page }"
            >
              <button class="page-link" @click="mudarPagina(p)">{{ p }}</button>
            </li>
            <li v-if="pageNumbers[pageNumbers.length - 1] < totalPages" class="page-item disabled">
              <span class="page-link">…</span>
            </li>
            <li class="page-item" :class="{ disabled: page === totalPages }">
              <button class="page-link" @click="mudarPagina(page + 1)">&raquo;</button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal Detalhe + Edição de Status -->
    <b-modal
      v-model="showModal"
      title="Detalhe do Chamado"
      hide-footer
      size="lg"
      @hidden="fecharModal"
    >
      <template v-if="modalChamado">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label fw-semibold text-muted small mb-1">ID do Protocolo</label>
            <p class="mb-0 font-monospace small text-break">{{ modalChamado.id }}</p>
          </div>

          <div class="col-sm-6">
            <label class="form-label fw-semibold text-muted small mb-1">Data / Hora</label>
            <p class="mb-0">{{ formatData(modalChamado["@timestamp"]) }}</p>
          </div>

          <div class="col-sm-6">
            <label class="form-label fw-semibold text-muted small mb-1">Telefone</label>
            <p class="mb-0">{{ modalChamado.contact_phone || "—" }}</p>
          </div>

          <div class="col-sm-6">
            <label class="form-label fw-semibold text-muted small mb-1">CNPJ Credencial</label>
            <p class="mb-0">{{ modalChamado.credencial_cnpj || "—" }}</p>
          </div>

          <div class="col-sm-6">
            <label class="form-label fw-semibold text-muted small mb-1">Status atual</label>
            <p class="mb-0">
              <span :class="['badge', statusBadge(modalChamado.status).cls]">
                {{ statusBadge(modalChamado.status).label }}
              </span>
            </p>
          </div>

          <div class="col-12">
            <label class="form-label fw-semibold text-muted small mb-1">Título</label>
            <p class="mb-0">{{ modalChamado.titulo || "—" }}</p>
          </div>

          <div class="col-12">
            <label class="form-label fw-semibold text-muted small mb-1">Descrição</label>
            <p class="mb-0" style="white-space: pre-wrap;">{{ modalChamado.descricao || "—" }}</p>
          </div>

          <div class="col-12">
            <label class="form-label fw-semibold text-muted small mb-1">Mensagem original do usuário</label>
            <p class="mb-0 fst-italic text-muted" style="white-space: pre-wrap;">{{ modalChamado.mensagem_literal || "—" }}</p>
          </div>

          <div class="col-12">
            <hr class="my-1" />
          </div>

          <!-- Edição de status -->
          <div class="col-sm-6">
            <label class="form-label fw-semibold mb-1">Alterar Status</label>
            <select v-model="novoStatus" class="form-select form-select-sm">
              <option value="aberto">Aberto</option>
              <option value="em_atendimento">Em Atendimento</option>
              <option value="fechado">Fechado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div class="col-sm-6 d-flex align-items-end">
            <button
              class="btn btn-primary btn-sm w-100"
              :disabled="savingStatus"
              @click="salvarStatus"
            >
              <span v-if="savingStatus" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Salvar Status
            </button>
          </div>

          <div v-if="saveStatusMsg" class="col-12">
            <div
              :class="['alert', 'py-2', 'mb-0',
                saveStatusMsg.type === 'success' ? 'alert-success' : 'alert-danger']"
            >
              {{ saveStatusMsg.text }}
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
